import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  FORMATNAME,
  ausgabeformat as holeAusgabeformat,
  ausgaben as holeAusgaben,
  datei as holeDatei,
  erzeugen,
  freigeben as sendeFreigabe,
  pruefbericht as holePruefbericht,
  versandauskunft as holeVersandauskunft,
  versenden,
  vorschau as holeVorschau,
  type Ausgabe,
  type Ausgabeformatauskunft,
  type Belegart,
  type Belegformat,
  type Pruefstand,
  type Versandauskunft,
} from '../api/belegausgabe';
import { ApiFehler } from '../api/fehler';
import { alsTag } from '../datum';
import { type Datei, ladeUndReiche } from '../komponenten/datei';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';

/**
 * Der Belegabschnitt — Vorschau, PDF und die Liste der Ausgaben.
 *
 * Er steht in der Angebots- und in der Auftragsakte, weil dieselbe Frage
 * dort zweimal gestellt wird: „wie sieht das aus, was der Kunde bekommt?"
 * (ANG-12, AUF-08).
 *
 * ZWEI KNÖPFE, DIE VERSCHIEDENES TUN:
 *
 *   Vorschau   zeigt HTML. Sie braucht den Setzdienst nicht — wer den
 *              Briefkopf ändert, sieht das Ergebnis auch dann, wenn der
 *              Renderer gerade nicht läuft.
 *   PDF        setzt das Dokument, legt es ab und vermerkt es. Das ist der
 *              Beleg, der aus dem Haus geht — und er ist danach
 *              unveränderlich (DAT-05).
 *
 * DREI SCHRITTE BIS ZUM KUNDEN, und keiner ist überspringbar (ANG-12):
 *
 *   erzeugen → **prüfen und freigeben** → versenden
 *
 * Der mittlere ist neu. Geprüft wird das ERZEUGTE DOKUMENT, nicht die
 * HTML-Vorschau: Die wird aus den heutigen Daten neu gebaut, das PDF ist
 * eingefroren. Wer die Vorschau prüft und das PDF versendet, hat bei einer
 * Preisänderung dazwischen etwas anderes gesehen als der Kunde bekommt.
 *
 * Die Freigabe nennt eine Person und ist danach nicht mehr änderbar. Sie ist
 * bewusst ein eigener Schritt und kein Haken am Versandformular: Ein Haken,
 * den man im selben Atemzug mit dem Absenden setzt, wird mitgeklickt.
 *
 * DIE VORSCHAU LÄUFT IN EINEM ABGESCHOTTETEN RAHMEN. Der Beleg bringt CSS
 * aus der Vorlage mit; ohne eigenen Kontext liefe es in die Maske hinein und
 * verschöbe sie. `sandbox=""` schließt zusätzlich aus, dass ein Skript aus
 * der Vorlage etwas tut — die Belegdaten maskiert der Server ohnehin, die
 * Vorlage selbst ist aber gestaltbar, und Gestaltung ist kein Freibrief.
 */
/**
 * Der Weg vom Entwurf zum Kunden (ANG-12, ANG-14).
 *
 * Er steht hier als Datum und nicht als drei Stellen im Markup: Wer ihn
 * ändert, ändert ihn einmal.
 */
const SCHRITTE: readonly { readonly name: string; readonly was: string }[] = [
  { name: 'Erzeugen', was: 'setzt das Dokument und legt es unveränderlich ab' },
  { name: 'Prüfen und freigeben', was: 'die letzte Sicht auf das, was hinausgeht' },
  { name: 'Versenden', was: 'in den Postausgang, aus dem Postfach des Anlasses' },
  { name: 'Versandt', was: 'übernommen vom Mailserver' },
];

/**
 * Ob das Format ein PDF ergibt (Auftrag CRM-168): Nur dann geht vorab ein
 * Reiter für den Betrachter auf. Eine XRechnung ist reines XML.
 */
function alsPdf(format: Belegformat): boolean {
  return format === 'pdf' || format === 'zugferd';
}

/**
 * Welches Recht am Erzeugen hängt — je Belegart ein anderes.
 *
 * Es steht hier, weil die Maske es NENNEN muss: Ein Knopf, der einfach fehlt,
 * ist von einem Fehler nicht zu unterscheiden. Wer ihn sucht und nicht
 * findet, hält die Maske für kaputt — und liegt damit nicht ganz falsch.
 */
const ERZEUGRECHT: Record<Belegart, string> = {
  angebot: 'angebot/versenden',
  auftrag: 'auftrag/bestaetigen',
  // C9.7: dasselbe Recht wie in der API (rechte/dekoratoren.ts).
  rechnung: 'rechnung/versenden',
};

export function Belegabschnitt({
  belegart,
  beleg,
  darfErzeugen,
  erzeugenGesperrt,
  empfaengerVorschlag = null,
}: {
  readonly belegart: Belegart;
  readonly beleg: string;
  readonly darfErzeugen: boolean;
  /**
   * C9.5: warum HIER kein Dokument entsteht, obwohl das Recht da ist — etwa
   * weil die Rechnung noch ein Entwurf ohne Nummer ist. Die Vorschau bleibt.
   */
  readonly erzeugenGesperrt?: string | undefined;
  /**
   * KUN-08, BUC-103: an wen der Beleg gehen soll — bei der Rechnung der
   * markierte Rechnungsempfänger, sonst die Adresse des Unternehmens. Steht
   * im Feld, wenn das Versandformular aufgeht, und bleibt änderbar.
   */
  readonly empfaengerVorschlag?: string | null | undefined;
}): JSX.Element {
  const [liste, setzeListe] = useState<readonly Ausgabe[]>([]);
  const [html, setzeHtml] = useState<string | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [versand, setzeVersand] = useState<string | undefined>(undefined);
  /*
   * ANG-12: Die Prüfung vor dem Versand.
   *
   * `pruefung` ist die Ausgabe, die gerade geprüft wird; `pruefUrl` das
   * geladene Dokument. Beides gehört zusammen und wird zusammen wieder
   * freigegeben — ein Objektverweis, der stehen bleibt, hält das ganze PDF im
   * Speicher.
   */
  const [pruefung, setzePruefung] = useState<Ausgabe | undefined>(undefined);
  const [pruefUrl, setzePruefUrl] = useState<string | undefined>(undefined);
  /*
   * SUP-19: aus welchem Postfach der Beleg geht. Sie wird geholt, wenn das
   * Formular aufgeht, und nicht erst beim Senden: Ein Absender, den man erst
   * hinterher im Postausgang sieht, fällt erst auf, wenn der Kunde geantwortet
   * hat — an die falsche Stelle.
   */
  const [auskunft, setzeAuskunft] = useState<Versandauskunft | undefined>(undefined);
  /*
   * RNG-15: In welchem Format die Rechnung hinausgeht. Sie wird GEZEIGT und
   * nicht gefragt — `fach.kondition.ausgabeformat` ist eine Eigenschaft des
   * Kunden. Wer sie ändern will, ändert sie am Kunden, nicht am Beleg.
   */
  const [format, setzeFormat] = useState<Ausgabeformatauskunft | undefined>(undefined);
  /** RNG-14: der Prüfbericht zu der Ausgabe, die gerade geprüft wird. */
  const [pruefstand, setzePruefstand] = useState<Pruefstand | null | undefined>(undefined);
  const [empfaenger, setzeEmpfaenger] = useState('');
  const [betreff, setzeBetreff] = useState('');
  const [text, setzeText] = useState('');
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  /* Auftrag CRM-103: welche Rückfrage gerade offen ist. */
  const [rueckfrage, setzeRueckfrage] = useState<'freigabe' | 'versand' | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    try {
      setzeListe(await holeAusgaben(belegart, beleg));
    } catch {
      setzeListe([]);
    }
    if (belegart !== 'rechnung') return;
    try {
      setzeFormat(await holeAusgabeformat(beleg));
    } catch {
      // Ohne die Auskunft bleibt der Knopf beschriftet wie bisher. Ein
      // fehlender Hinweis darf das Erzeugen nicht verhindern.
      setzeFormat(undefined);
    }
  }, [belegart, beleg]);

  useEffect(() => {
    void laden();
  }, [laden]);

  /**
   * Holt den Beleg und reicht ihn (Auftrag CRM-168): ein PDF öffnet gleich im
   * Betrachter. Muss im Klick vor dem ersten `await` stehen, sonst sperrt
   * Safari den Reiter.
   */
  async function reiche(laden: () => Promise<Datei>, erwartePdf: boolean): Promise<void> {
    const hinweis = await ladeUndReiche(laden, erwartePdf);
    if (hinweis !== null) setzeRueckmeldung(hinweis);
  }

  /** Schließt die Prüfansicht und gibt den Objektverweis frei. */
  const schliessePruefung = useCallback((): void => {
    setzePruefUrl((bisher) => {
      if (bisher !== undefined) URL.revokeObjectURL(bisher);
      return undefined;
    });
    setzePruefung(undefined);
    setzePruefstand(undefined);
  }, []);

  // Auch beim Verlassen der Maske — sonst bleibt das PDF im Speicher liegen.
  useEffect(() => schliessePruefung, [schliessePruefung]);

  async function handle(was: () => Promise<void>): Promise<void> {
    try {
      await bestaetigt(was);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  /**
   * Wie `handle`, nur WIRFT ein Fehler weiter: Die Rückfrage (Auftrag CRM-103)
   * zeigt ihn in ihrem Pop-up und bleibt offen.
   */
  async function bestaetigt(was: () => Promise<void>): Promise<void> {
    setzeLaeuft(true);
    try {
      await was();
      setzeFehler(undefined);
    } finally {
      setzeLaeuft(false);
    }
  }

  /* Das Dokument, das gerade zum Versand offen ist — für die Rückfrage. */
  const zuVersenden = liste.find((a) => a.id === versand);

  /*
   * Wo im Weg wir stehen. Sie richtet sich nach dem WEITESTEN Dokument:
   * Liegt eines zur Freigabe, ist Schritt 2 dran, auch wenn daneben schon ein
   * versandtes steht.
   */
  const stand = liste.length === 0
    ? 0
    : liste.some((a) => a.geprueftAm === null && a.versendetAm === null)
      ? 1
      : liste.some((a) => a.versendetAm === null)
        ? 2
        : 3;

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Beleg</h2>
        {/* ANG-12, AUF-08 */}
      </div>

      {fehler !== undefined && <Meldung fehler={fehler} />}

      {/*
        DIE VIER SCHRITTE STEHEN DA, BEVOR ES SIE GIBT.

        „Prüfen" und „Versenden" gehören zu einem erzeugten Dokument und
        erscheinen erst mit ihm in der Liste darunter. Solange keines da ist,
        sah der Abschnitt aus wie ein Ende: zwei Knöpfe, sonst nichts — und
        wer den Weg nicht kennt, sucht die Freigabe in der Vorschau, wo sie
        nicht ist. Der Weg wird deshalb ausgeschrieben, samt der Stelle, an
        der man gerade steht.
      */}
      <ol className="schrittfolge">
        {SCHRITTE.map((schritt, i) => (
          <li
            key={schritt.name}
            className={`schrittfolge__schritt${i === stand ? ' schrittfolge__schritt--hier' : ''}${
              i < stand ? ' schrittfolge__schritt--erledigt' : ''
            }`}
            aria-current={i === stand ? 'step' : undefined}
          >
            <span className="schrittfolge__nummer">{i + 1}</span>
            <span>
              {schritt.name}
              <span className="kanalliste__name"> · {schritt.was}</span>
            </span>
          </li>
        ))}
      </ol>

      <div className="knopfreihe">
        {html !== undefined && (
          <button type="button" className="knopf" onClick={() => setzeHtml(undefined)}>
            Vorschau schließen
          </button>
        )}
        <button
          type="button"
          className="knopf"
          disabled={laeuft}
          onClick={() =>
            void handle(async () => {
              setzeHtml(await holeVorschau(belegart, beleg));
            })
          }
        >
          Vorschau
        </button>
        {darfErzeugen && erzeugenGesperrt === undefined && (
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={laeuft}
            onClick={() =>
              void handle(async () => {
                await reiche(() => erzeugen(belegart, beleg), format === undefined || alsPdf(format.format));
                await laden();
              })
            }
          >
            {laeuft
              ? 'Wird erzeugt …'
              : format === undefined
                ? 'PDF erzeugen'
                /*
                  Der Knopf sagt, WAS entsteht. „PDF erzeugen" an einer
                  Rechnung, die als XRechnung hinausgeht, wäre schlicht
                  falsch — und man merkte es erst an der heruntergeladenen
                  Datei.
                */
                : `${FORMATNAME[format.format].split(' (')[0]!} erzeugen`}
          </button>
        )}
      </div>

      {/* ANG-13 */}
      <p className="feld__hilfe">
        Die Vorlage wird nach der Kundenart vorausgewählt. Die <strong>Vorschau</strong>
        {' '}zeigt dasselbe Markup, das zu PDF wird — sie braucht den Setzdienst nicht und ist{' '}
        <em>nicht</em> das, was freigegeben wird: Sie entsteht aus den heutigen Daten, das PDF ist
        eingefroren.
      </p>

      {/*
        EIN FEHLENDES RECHT WIRD BENANNT, NICHT VERSCHWIEGEN.

        Ohne `darfErzeugen` fehlte der Knopf einfach — und ein fehlender Knopf
        ist von einem Fehler nicht zu unterscheiden. Wer ihn sucht, hält die
        Maske für kaputt. Hier steht stattdessen, welches Recht fehlt; damit
        lässt sich die Frage an die Administration in einem Satz stellen.
      */}
      {!darfErzeugen && (
        <div className="hinweiskarte hinweiskarte--warnung">
          <p>
            <strong>Sie dürfen hier kein Dokument erzeugen.</strong> Dafür ist das Recht{' '}
            <code>{ERZEUGRECHT[belegart]}</code> nötig — mit ihm hängen auch Prüfen und
            Versenden zusammen.
          </p>
          <p className="feld__hilfe">
            Ansehen und herunterladen können Sie bestehende Dokumente weiterhin. Wer das Recht
            vergibt, sieht es unter Benutzer und Rollen.
          </p>
        </div>
      )}

      {darfErzeugen && erzeugenGesperrt !== undefined && (
        <p className="hinweiskarte" role="status">
          {erzeugenGesperrt}
        </p>
      )}

      {darfErzeugen && erzeugenGesperrt === undefined && liste.length === 0 && (
        <p className="hinweiskarte">
          Noch kein Dokument erzeugt. <strong>Prüfen und Versenden</strong> stehen zur Verfügung,
          sobald eines da ist — beide gehören zu einem bestimmten Dokument und erscheinen an
          seiner Zeile.
        </p>
      )}

      {/*
        RNG-15: Das Format steht am Kunden und wird hier nur BERICHTET. Eine
        Auswahlliste an dieser Stelle lüde dazu ein, es je Beleg anders zu
        machen — und dann bekäme derselbe Kunde mal ein PDF und mal eine
        XRechnung, je nachdem, wer die Rechnung erzeugt hat.
      */}
      {format !== undefined && (
        <div className={format.hinweis === null ? 'hinweiskarte' : 'hinweiskarte hinweiskarte--warnung'}>
          <p>
            Geht als <strong>{FORMATNAME[format.format]}</strong> hinaus
            {format.herkunft === 'kunde'
              ? ' — so ist es beim Empfänger hinterlegt.'
              : ' — das ist die Voreinstellung; beim Empfänger ist nichts hinterlegt.'}
          </p>
          {format.hinweis !== null && <p><strong>{format.hinweis}</strong></p>}

          <p className="feld__hilfe">
            Zu ändern ist das in der Kundenakte unter den Konditionen, nicht hier.
          </p>
        </div>
      )}

      {html !== undefined && (
        <iframe
          title="Belegvorschau"
          srcDoc={html}
          sandbox=""
          className="belegvorschau belegvorschau--niedrig"
        />
      )}

      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {/*
        ANG-12: DIE LETZTE PRÜFUNG.

        Gezeigt wird das ERZEUGTE DOKUMENT und nicht die HTML-Vorschau. Die
        Vorschau wird aus den heutigen Daten neu gebaut; das PDF ist
        eingefroren. Wer die Vorschau prüft und das PDF versendet, hat etwas
        anderes gesehen als der Kunde bekommt — bei einer Preisänderung
        zwischen Erzeugen und Versenden genau das Falsche.
      */}
      {pruefung !== undefined && (
        <div className="karte">
          <div className="karte__titel">
            <h2>Prüfen und freigeben</h2>
            {/* ANG-12 */}
          </div>

          <p className="feld__hilfe">
            Das ist <strong>{pruefung.dateiname}</strong> — genau die Datei, die dem Kunden
            zugeht. Nach der Freigabe ist sie unveränderlich, und die Freigabe selbst wird mit
            Ihrem Namen festgehalten.
          </p>

          {/*
            RNG-14: „Fehler werden im Klartext und mit Bezug zum Feld
            angezeigt." Deshalb je Befund eine Zeile mit Regel, Feld und Satz
            — und nicht ein gesammeltes „ungültig", mit dem niemand etwas
            anfangen kann.
          */}
          {pruefstand !== undefined && pruefstand !== null && (
            <div className={pruefstand.gueltig ? 'hinweiskarte' : 'hinweiskarte hinweiskarte--warnung'}>
              <p>
                {pruefstand.gueltig ? (
                  <>
                    Vom Prüfdienst <strong>angenommen</strong> — keine Beanstandung.
                  </>
                ) : (
                  <>
                    Der Prüfdienst hat <strong>{pruefstand.befunde.length} Beanstandung(en)</strong>.
                    Ein Empfänger, der nach demselben Regelwerk prüft, weist den Beleg ab.
                  </>
                )}
              </p>
              <p className="feld__hilfe">
                Regelwerk: {pruefstand.regelwerk} · geprüft am{' '}
                {pruefstand.geprueftAm.slice(0, 19).replace('T', ' ')}
              </p>
              {pruefstand.befunde.length > 0 && (
                <div className="tabellenhuelle tabellenhuelle--telefon">
                  <table className="liste__tabelle">
                    <thead>
                      <tr>
                        <th scope="col">Regel</th>
                        <th scope="col">Feld</th>
                        <th scope="col">Beanstandung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pruefstand.befunde.map((b, i) => (
                        <tr key={`${b.regel}-${String(i)}`}>
                          <td>
                            <span className={b.schwere === 'warnung' ? 'marke marke--inaktiv' : 'marke'}>
                              {b.regel}
                            </span>
                          </td>
                          <td>{b.feld}</td>
                          <td>{b.meldung}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {pruefUrl !== undefined && (
            <iframe
              title={`Prüfansicht ${pruefung.dateiname}`}
              src={pruefUrl}
              className="belegvorschau"
            />
          )}

          <div className="knopfreihe">
            <button type="button" className="knopf" onClick={schliessePruefung}>
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laeuft || pruefUrl === undefined}
              onClick={() => setzeRueckfrage('freigabe')}
            >
              Freigeben …
            </button>
          </div>

          <p className="feld__hilfe">
            Ohne Freigabe geht nichts hinaus — auch nicht über einen anderen Weg als diese
            Maske. Die Sperre liegt in der Datenbank.
          </p>
        </div>
      )}

      {/*
        ANG-14, RNG-18: Der Versand geht in die Outbox. Die Maske sagt es —
        „gesendet" wäre eine Behauptung, die weder SMTP noch Graph deckt: Beide
        bestätigen nur die Übernahme durch den ersten Server.
      */}
      {versand !== undefined && (
        <div className="karte">
          <div className="karte__titel">
            <h2>Versenden</h2>
            {/* ANG-14, RNG-18, SUP-19 */}
          </div>

          {/*
            Das Postfach steht ÜBER dem Formular und nicht darunter: Es ist
            die Angabe, die man liest, bevor man tippt — nicht die, die man
            nach dem Absenden sucht.
          */}
          {auskunft !== undefined && (
            <div className={auskunft.gefunden && auskunft.versandbereit ? 'hinweiskarte' : 'hinweiskarte hinweiskarte--warnung'}>
              {!auskunft.gefunden ? (
                <p>
                  Für <strong>{auskunft.anlass}</strong>
                  {auskunft.kundenart === null ? '' : ` an ${auskunft.kundenart}`} ist{' '}
                  <strong>kein Postfach hinterlegt</strong>. Legen Sie in den Einstellungen
                  eine Postfachregel an — ohne sie hat die Nachricht keinen Absender, unter
                  dem der Kunde antworten könnte.
                </p>
              ) : (
                <>
                  <p>
                    Geht aus <strong>{auskunft.bezeichnung}</strong> ({auskunft.kennung}) —{' '}
                    {auskunft.absenderName} &lt;{auskunft.absender}&gt;
                  </p>
                  <p className="feld__hilfe">
                    Regel: {auskunft.regel}
                    {auskunft.kundenart === null ? '' : ` · Kundenart: ${auskunft.kundenart}`}
                  </p>
                  {!auskunft.aktiv && (
                    <p>
                      <strong>Dieses Postfach ist noch nicht freigegeben.</strong> Hinterlegen
                      Sie Absenderadresse und Zugangsdaten und schalten Sie es frei; bis dahin
                      geht aus ihm nichts hinaus.
                    </p>
                  )}
                  {auskunft.aktiv && !auskunft.versandbereit && (
                    <p>
                      <strong>Für dieses Postfach fehlen die Zugangsdaten.</strong> Die
                      Nachricht ließe sich einstellen, ginge aber nicht hinaus.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="vs-empfaenger">
              Empfänger
            </label>
            <input
              id="vs-empfaenger"
              type="email"
              value={empfaenger}
              onChange={(e) => setzeEmpfaenger(e.target.value)}
              placeholder="kunde@beispiel.de"
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="vs-betreff">
              Betreff
            </label>
            <input
              id="vs-betreff"
              type="text"
              value={betreff}
              onChange={(e) => setzeBetreff(e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="vs-text">
              Text
            </label>
            <textarea
              id="vs-text"
              rows={6}
              value={text}
              onChange={(e) => setzeText(e.target.value)}
            />
          </div>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeVersand(undefined);
                setzeAuskunft(undefined);
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              /*
                Kein Postfach, keine Freigabe — kein Versand. Der Dienst weist
                es ohnehin ab (ERR-01); der Knopf sagt es vorher, statt den
                Anwender einen Text schreiben zu lassen, der nicht hinausgeht.
              */
              disabled={
                empfaenger === ''
                || betreff === ''
                || laeuft
                || auskunft === undefined
                || !auskunft.gefunden
                || !auskunft.aktiv
              }
              onClick={() => setzeRueckfrage('versand')}
            >
              In den Postausgang …
            </button>
          </div>
          {/* ERR-08 */}
          <p className="feld__hilfe">
            Die Nachricht geht in den <strong>Postausgang</strong> und nicht unmittelbar hinaus:
            Ein Mailserver, der nicht antwortet, hält die Maske nicht an. Ein zweiter
            Versand desselben Dokuments ist ausgeschlossen — er wäre beim Kunden eine zweite
            Rechnung.
          </p>
        </div>
      )}

      {liste.length > 0 && (
        <>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption>{liste.length} erzeugte Dokumente</caption>
              <thead>
                <tr>
                  <th scope="col">Datei</th>
                  <th scope="col">Vorlage</th>
                  <th scope="col">erzeugt</th>
                  <th scope="col">von</th>
                  <th scope="col">Freigabe</th>
                  <th scope="col">Versand</th>
                  <th scope="col">Handlung</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((a) => (
                  <tr key={a.id}>
                    <td>
                      {a.dateiname}
                      {/*
                        Ein ZUGFeRD-Beleg und ein gewöhnliches PDF heißen beide
                        „.pdf" — nur ist das eine eine E-Rechnung und das andere
                        ein Bild von einer Rechnung. Der Unterschied gehört
                        sichtbar an die Zeile.
                      */}
                      <span className="kanalliste__name"> · {FORMATNAME[a.format]}</span>
                    </td>
                    <td>
                      <span className="liste__nummer">{a.vorlagenkennung}</span>
                      <span className="kanalliste__name"> · Fassung {a.vorlagenversion}</span>
                    </td>
                    <td>{a.erzeugtAm.slice(0, 19).replace('T', ' ')}</td>
                    <td>{a.erzeugtVonName ?? '—'}</td>
                    <td>
                      {a.geprueftAm === null ? (
                        <span className="marke marke--inaktiv">nicht geprüft</span>
                      ) : (
                        <>
                          <span className="marke">freigegeben</span>
                          <span className="kanalliste__name">
                            {' '}
                            · {a.geprueftVonName ?? 'unbekannt'},{' '}
                            <span className="liste__einzeilig">{alsTag(a.geprueftAm)}</span>
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      {a.versendetAm === null ? (
                        <span className="marke marke--inaktiv">nicht versendet</span>
                      ) : (
                        <>
                          {a.empfaenger}
                          {/*
                            "übernommen" und nicht "zugestellt": Weder SMTP noch
                            Graph bestätigen die Ankunft, nur die Übernahme durch
                            den ersten Server.
                          */}
                          <span className="kanalliste__name">
                            {' '}
                            · {a.versandergebnis === 'unbekannt' ? 'übernommen' : a.versandergebnis}
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        onClick={() =>
                          void handle(async () => {
                            await reiche(
                              async () => ({ blob: await holeDatei(a.id), dateiname: a.dateiname }),
                              alsPdf(a.format),
                            );
                          })
                        }
                      >
                        Herunterladen
                      </button>
                      {/*
                        ANG-12: Erst prüfen, dann versenden. Die beiden Knöpfe
                        stehen nie gleichzeitig da — sonst ließe sich die
                        Prüfung überspringen, ohne sie zu bemerken.
                      */}
                      {darfErzeugen && a.versendetAm === null && a.geprueftAm === null && (
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() =>
                            void handle(async () => {
                              schliessePruefung();
                              setzeVersand(undefined);
                              const blob = await holeDatei(a.id);
                              setzePruefUrl(URL.createObjectURL(blob));
                              setzePruefung(a);
                              /*
                                RNG-14: Der Prüfbericht gehört NEBEN das
                                Dokument, nicht in eine eigene Maske. Wer
                                freigibt, soll beides zugleich sehen — sonst
                                gibt er frei, ohne den Befund gelesen zu haben.
                              */
                              setzePruefstand(await holePruefbericht(a.id));
                            })
                          }
                        >
                          Prüfen …
                        </button>
                      )}
                      {darfErzeugen && a.versendetAm === null && a.geprueftAm !== null && (
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() => {
                            schliessePruefung();
                            setzeVersand(a.id);
                            setzeAuskunft(undefined);
                            setzeEmpfaenger((bisher) => (bisher === '' ? (empfaengerVorschlag ?? '') : bisher));
                            setzeBetreff(a.dateiname.replace(/\.pdf$/, ''));
                            setzeText('Guten Tag,\n\nanbei erhalten Sie den Beleg.\n\nMit freundlichen Grüßen');
                            void handle(async () => {
                              setzeAuskunft(await holeVersandauskunft(a.id));
                            });
                          }}
                        >
                          Versenden …
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="feld__hilfe">
            Jedes erzeugte Dokument bleibt abgelegt und unveränderlich; die Fassung der
            Vorlage steht dabei, weil ein im März erzeugter Beleg im November noch sagen können
            muss, woraus er entstand.
          </p>
        </>
      )}

      {rueckfrage === 'freigabe' && pruefung !== undefined && (
        <Rueckfrage
          frage="Dokument freigeben?"
          unterzeile={pruefung.dateiname}
          tat="Freigeben"
          beiSchliessen={() => setzeRueckfrage(null)}
          beiBestaetigen={() =>
            bestaetigt(async () => {
              await sendeFreigabe(pruefung.id);
              setzeRueckfrage(null);
              setzeRueckmeldung(`${pruefung.dateiname} ist freigegeben und kann versandt werden.`);
              schliessePruefung();
              await laden();
            })
          }
        >
          {/* ANG-12 */}
          <p>
            <strong>{pruefung.dateiname}</strong> wird mit Ihrem Namen freigegeben und kann danach
            versandt werden. Die Freigabe ist danach nicht mehr änderbar.
          </p>
          {/* RNG-14 */}
          {pruefstand !== undefined && pruefstand !== null && !pruefstand.gueltig && (
            <p>
              Der Prüfdienst hat <strong>{pruefstand.befunde.length} Beanstandung(en)</strong> —
              ein Empfänger, der nach demselben Regelwerk prüft, weist den Beleg ab.
            </p>
          )}
        </Rueckfrage>
      )}

      {rueckfrage === 'versand' && versand !== undefined && (
        <Rueckfrage
          frage="Dokument versenden?"
          unterzeile={zuVersenden?.dateiname}
          tat="In den Postausgang"
          beiSchliessen={() => setzeRueckfrage(null)}
          beiBestaetigen={() =>
            bestaetigt(async () => {
              const ergebnis = await versenden(versand, {
                empfaenger: [empfaenger],
                betreff,
                text,
              });
              setzeRueckfrage(null);
              setzeRueckmeldung(
                ergebnis.neu
                  ? 'In den Postausgang eingestellt. Der Versandlauf nimmt ihn mit.'
                  : 'Dieser Beleg war bereits eingestellt — es entsteht keine zweite Nachricht.',
              );
              setzeVersand(undefined);
              setzeAuskunft(undefined);
              await laden();
            })
          }
        >
          {/* ANG-14, RNG-18, SUP-19 */}
          <p>
            {zuVersenden === undefined ? 'Das Dokument' : <strong>{zuVersenden.dateiname}</strong>}{' '}
            geht an <strong>{empfaenger}</strong> mit dem Betreff „{betreff}“
            {auskunft !== undefined && auskunft.gefunden && (
              <>
                {' '}— aus {auskunft.bezeichnung}, {auskunft.absenderName} &lt;{auskunft.absender}&gt;
              </>
            )}
            .
          </p>
          {/* ERR-08 */}
          <p>
            Die Nachricht geht in den Postausgang. Ein zweiter Versand desselben Dokuments ist
            ausgeschlossen.
          </p>
        </Rueckfrage>
      )}
    </div>
  );
}
